import { View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Animated from "react-native-reanimated";
import GetUserBasicInfoDocument from "src/api/graphql/__generated__/documents/GetUserBasicInfoDocument";
import FaceFilterMode from "src/api/graphql/__generated__/enums/FaceFilterMode";
import OpacityView from "src/components/OpacityView";
import PillButton from "src/components/PillButton";
import ProfilePicture from "src/components/ProfilePicture";
import RoundedButton from "src/components/RoundedButton";
import { useGatheringStore } from "src/contexts/GatheringContext";
import useAnimatedOpacityStyle from "src/hooks/useAnimatedOpacityStyle";
import useRevalidatedQuery from "src/hooks/useRevalidatedQuery";
import IconClose from "src/icons/IconClose";
import IconFilter from "src/icons/IconFilter";
import { paddingMedium, paddingSmall, paddingXL } from "src/styles/spacing";
import { AnimatePresence, Text } from "src/swsh-native";
import { GatheringViewerHorizontalPadding } from "../constants";
import SectionHeader from "./SectionHeader";

const UserPill = ({ userId }: { userId: string }) => {
	const { data, loading } = useRevalidatedQuery(GetUserBasicInfoDocument, {
		variables: {
			input: {
				userId,
			},
		},
	});
	const profile = data?.getUserBasicInfo;
	const toggleFilterUserId = useGatheringStore((state) => state.toggleFilterUserId);

	return (
		<OpacityView exiting={false}>
			<PillButton
				loading={loading && !profile}
				CustomIcon={
					<ProfilePicture
						userId={userId}
						size={24}
						style={{ marginLeft: paddingMedium }}
					/>
				}
				ActionIcon={IconClose}
				colorVariant="Alt"
				onPress={() => {
					toggleFilterUserId(userId);
				}}
			>
				{profile?.firstName ?? undefined}
			</PillButton>
		</OpacityView>
	);
};

const GatheringViewersFilterSection = () => {
	const filterUserIds = useGatheringStore((state) => state.filterUserIds);
	const filterMode = useGatheringStore((state) => state.filterMode);
	const showButton = filterUserIds.length >= 2;
	const animatedOpacity = useAnimatedOpacityStyle(showButton ? 1 : 0);
	const setFilterMode = useGatheringStore((state) => state.setFilterMode);

	return (
		<View
			style={{
				height: 118,
				paddingBottom: paddingXL,
			}}
		>
			<SectionHeader
				Icon={IconFilter}
				text="Filters"
				action={
					<Animated.View
						style={animatedOpacity}
						pointerEvents={showButton ? "auto" : "none"}
					>
						<RoundedButton
							onPress={() => {
								if (filterMode === FaceFilterMode.And) {
									setFilterMode(FaceFilterMode.Or);
								} else {
									setFilterMode(FaceFilterMode.And);
								}
							}}
							sizeVariant="SkinnySmall"
							colorVariant={filterMode === FaceFilterMode.Or ? "Hollow" : "Contrast"}
						>
							Together
						</RoundedButton>
					</Animated.View>
				}
			/>
			{filterUserIds.length > 0 ? (
				<ScrollView
					horizontal
					contentContainerStyle={{
						columnGap: paddingMedium,
						paddingHorizontal: GatheringViewerHorizontalPadding,
						alignItems: "center",
					}}
				>
					<AnimatePresence>
						{filterUserIds.map((userId) => (
							<UserPill key={userId} userId={userId} />
						))}
					</AnimatePresence>
				</ScrollView>
			) : (
				<Text
					style={{
						marginTop: paddingSmall,
						marginHorizontal: GatheringViewerHorizontalPadding,
					}}
					color="TextLight"
				>
					No filters applied
				</Text>
			)}
		</View>
	);
};

export default GatheringViewersFilterSection;
